import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useAllInvitations, getInvitationStatus } from '@/hooks/useInvitations';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, MailCheck, RefreshCw, Search, ChevronLeft, ChevronRight, Send, Trash2 } from 'lucide-react';

const PAGE_SIZE = 10;

export const InvitationsTable = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: invitations = [], isLoading, isFetching, refetch } = useAllInvitations();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<string | null>(null);

  const handleResend = async (invitation: any) => {
    setBusyId(invitation.id);
    try {
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      const { error: updateError } = await supabase
        .from('invitations')
        .update({ expires_at: expiresAt })
        .eq('id', invitation.id);
      if (updateError) throw updateError;

      const { error: fnError } = await supabase.functions.invoke('send-invitation-email', {
        body: {
          email: invitation.email,
          first_name: invitation.first_name,
          last_name: invitation.last_name,
          token: invitation.token,
          company_name: invitation.companies?.name ?? '',
          role_name: invitation.roles?.name ?? '',
          invitation_url: `${window.location.origin}/invite/${invitation.token}`,
        },
      });
      if (fnError) throw fnError;

      toast({
        title: t('adminInvitations.resendSuccess'),
        description: t('adminInvitations.resendSuccessDescription', { email: invitation.email }),
      });
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    } catch (error) {
      console.error('Resend invitation error:', error);
      toast({
        title: t('common.error'),
        description: t('adminInvitations.resendError'),
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (invitationId: string) => {
    setBusyId(invitationId);
    try {
      const { error } = await supabase.from('invitations').delete().eq('id', invitationId);
      if (error) throw error;
      toast({ title: t('adminInvitations.deleteSuccess') });
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    } catch (error) {
      console.error('Delete invitation error:', error);
      toast({
        title: t('common.error'),
        description: t('adminInvitations.deleteError'),
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
      setToDelete(null);
    }
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });

  const badgeClass = (status: string) =>
    status === 'accepted'
      ? 'bg-[#14A38B]/10 text-[#14A38B] border-[#14A38B]/20'
      : status === 'expired'
        ? 'bg-[#FF7171]/10 text-[#FF7171] border-[#FF7171]/20'
        : 'bg-[#F2AC57]/10 text-[#F2AC57] border-[#F2AC57]/20';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invitations;
    return invitations.filter((inv) =>
      [
        inv.first_name,
        inv.last_name,
        inv.email,
        inv.companies?.name,
        inv.roles?.name,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [invitations, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const rows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="bg-white rounded-xl border border-[#DBE2EA] shadow-md p-6 mt-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <MailCheck size={20} className="text-[#0880AE]" />
          <h2 className="text-xl font-medium text-[#37699E] font-['Lexend_Deca']">
            {t('adminInvitations.title')}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <SearchInput
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={t('adminInvitations.searchPlaceholder')}
              containerClassName="w-full md:w-64"
              className="w-full border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 rounded-lg font-['Funnel_Sans'] text-sm"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label={t('adminInvitations.refresh')}
            className="border-[#DBE2EA] text-[#5A6B7A] hover:text-[#0880AE] hover:bg-[#EBF4F8] rounded-lg"
          >
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-[#0880AE]" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-[#5A6B7A] py-4">{t('adminInvitations.empty')}</p>
      ) : (
        <>
          <table className="w-full table-fixed text-xs md:text-sm">
            <thead>
              <tr className="text-left text-[10px] md:text-xs font-medium text-[#5A6B7A] uppercase tracking-wide border-b border-[#DBE2EA]">
                <th className="py-3 pr-3 w-[16%]">{t('adminInvitations.person')}</th>
                <th className="py-3 pr-3 w-[20%]">{t('adminInvitations.email')}</th>
                <th className="py-3 pr-3 w-[15%]">{t('adminInvitations.company')}</th>
                <th className="py-3 pr-3 w-[11%]">{t('adminInvitations.role')}</th>
                <th className="py-3 pr-3 w-[9%]">{t('adminInvitations.sentAt')}</th>
                <th className="py-3 pr-3 w-[9%]">{t('adminInvitations.expiresAt')}</th>
                <th className="py-3 pr-3 w-[11%]">{t('adminInvitations.status')}</th>
                <th className="py-3 w-[9%] text-right">{t('adminInvitations.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((invitation) => {
                const status = getInvitationStatus(invitation);
                const person =
                  [invitation.first_name, invitation.last_name].filter(Boolean).join(' ') || '—';
                return (
                  <tr key={invitation.id} className="border-b border-[#EBF4F8] text-[#2D3748]">
                    <td className="py-3 pr-3 truncate" title={person}>{person}</td>
                    <td className="py-3 pr-3 truncate" title={invitation.email}>{invitation.email}</td>
                    <td className="py-3 pr-3 truncate" title={invitation.companies?.name ?? ''}>
                      {invitation.companies?.name ?? '—'}
                    </td>
                    <td className="py-3 pr-3 truncate" title={invitation.roles?.name ?? ''}>
                      {invitation.roles?.name ?? '—'}
                    </td>
                    <td className="py-3 pr-3 whitespace-nowrap">{formatDate(invitation.created_at)}</td>
                    <td className="py-3 pr-3 whitespace-nowrap">{formatDate(invitation.expires_at)}</td>
                    <td className="py-3 pr-3">
                      <Badge className={`${badgeClass(status)} border font-['Funnel_Sans'] whitespace-nowrap`}>
                        {t(`adminInvitations.statuses.${status}`)}
                      </Badge>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center justify-end gap-2">
                        {status !== 'accepted' && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={busyId === invitation.id}
                            onClick={() => handleResend(invitation)}
                            aria-label={t('adminInvitations.resend')}
                            title={t('adminInvitations.resend')}
                            className="h-8 w-8 border-[#DBE2EA] text-[#0880AE] hover:bg-[#EBF4F8] rounded-lg"
                          >
                            {busyId === invitation.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Send size={14} />
                            )}
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          disabled={busyId === invitation.id}
                          onClick={() => setToDelete(invitation.id)}
                          aria-label={t('adminInvitations.delete')}
                          title={t('adminInvitations.delete')}
                          className="h-8 w-8 border-[#DBE2EA] text-[#FF7171] hover:bg-[#FF7171]/10 rounded-lg"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex items-center justify-between gap-3 pt-4">
            <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans']">
              {t('adminInvitations.pageInfo', { page: currentPage, total: totalPages, count: filtered.length })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="border-[#DBE2EA] text-[#37699E] hover:bg-[#EBF4F8] rounded-lg"
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="border-[#DBE2EA] text-[#37699E] hover:bg-[#EBF4F8] rounded-lg"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#37699E] font-['Lexend_Deca']">
              {t('adminInvitations.deleteTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A6B7A] font-['Funnel_Sans']">
              {t('adminInvitations.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => toDelete && handleDelete(toDelete)}
              className="bg-[#FF7171] hover:bg-[#E65959] text-white rounded-lg"
            >
              {t('adminInvitations.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
